import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, X } from 'lucide-react';
import { type AppToolLink, buildLinkPrefill, isLinkOffered, linkLaunchValues, missingRequiredInputs } from '@shared/app-tool-links';
import { Button } from '@/components/ui';
import { launchLinkedTool } from '@/lib/app-tool-links';
import type { AppCollection, AppRecord } from '@/lib/app-records';
import { recordTitle } from './app-records-model';

type Launch = { link: AppToolLink; done: number; total: number; failed: string[]; finished: boolean };

/** Per selected object: what it gives the tool, and whether the form would still ask something. */
const plan = (link: AppToolLink, records: AppRecord[], collection: AppCollection) => {
  const offered = records.filter((record) => !record.archivedAt && isLinkOffered(link, record.status));
  const withValues = offered.map((record) => ({ record, prefill: buildLinkPrefill(link, record.computed, collection.columns) }));
  return {
    ready: withValues.filter((item) => missingRequiredInputs(link, item.prefill).length === 0),
    toComplete: withValues.filter((item) => missingRequiredInputs(link, item.prefill).length > 0),
  };
};

/**
 * S1 — several objects selected: one launch of the proposed tool per object,
 * after a confirmation that says how many. An object whose values leave a
 * required field empty is not launched; it is opened one by one from its drawer.
 */
export const AppLinkedToolsBar = ({ links, records, collection, onClear }: {
  links: AppToolLink[];
  records: AppRecord[];
  collection: AppCollection;
  onClear: () => void;
}) => {
  const [confirming, setConfirming] = useState<AppToolLink | null>(null);
  const [launch, setLaunch] = useState<Launch | null>(null);
  const plans = links.map((link) => ({ link, ...plan(link, records, collection) }))
    .filter((item) => item.ready.length + item.toComplete.length > 0);
  const running = launch !== null && !launch.finished;

  const start = async (link: AppToolLink) => {
    const items = plan(link, records, collection).ready;
    setConfirming(null);
    setLaunch({ link, done: 0, total: items.length, failed: [], finished: false });
    // One after the other: each launch is a run of its own, like a click in the form.
    for (const item of items) {
      try {
        await launchLinkedTool(link, linkLaunchValues(link, item.prefill));
        setLaunch((current) => current && { ...current, done: current.done + 1 });
      } catch {
        setLaunch((current) => current && { ...current, failed: [...current.failed, recordTitle(item.record, collection)] });
      }
    }
    setLaunch((current) => current && { ...current, finished: true });
  };

  return (
    <div role="region" aria-label="Sélection" className="flex flex-wrap items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm">
      <span className="font-medium text-domino-ink">{records.length} sélectionnée{records.length > 1 ? 's' : ''}</span>
      {confirming ? (() => {
        const target = plans.find((item) => item.link.id === confirming.id);
        const count = target?.ready.length ?? 0;
        return <>
          <span className="text-domino-ink">Lancer « {confirming.target.title} » pour {count} objet{count > 1 ? 's' : ''} ?</span>
          <Button size="sm" onClick={() => void start(confirming)}>Lancer</Button>
          <Button size="sm" variant="secondary" onClick={() => setConfirming(null)}>Annuler</Button>
        </>;
      })() : launch ? <>
        <span role="status" className="text-domino-ink">
          {launch.finished
            ? `${launch.done} lancement${launch.done > 1 ? 's' : ''} de « ${launch.link.target.title} »`
            : `Lancement ${launch.done + launch.failed.length + 1} sur ${launch.total}`}
        </span>
        {launch.failed.length > 0 && <span role="alert" className="text-[var(--domino-critical)]">Non lancés : {launch.failed.join(', ')}</span>}
        {launch.finished && <Link to={`/apps/${encodeURIComponent(launch.link.target.appKey)}`} className="text-domino-aubergine underline underline-offset-4">Voir</Link>}
      </> : plans.map(({ link, ready, toComplete }) => (
        <span key={link.id} className="inline-flex items-center gap-1.5">
          {ready.length > 0 && (
            <Button size="sm" variant="secondary" className="gap-1.5" onClick={() => setConfirming(link)}>
              <Play className="h-3 w-3 fill-current" aria-hidden="true" />{link.target.title} · {ready.length}
            </Button>
          )}
          {toComplete.length > 0 && (
            <span className="text-xs text-domino-inkSoft" title={toComplete.map((item) => recordTitle(item.record, collection)).join(', ')}>
              {ready.length > 0 ? '+ ' : `${link.target.title} : `}{toComplete.length} à compléter
            </span>
          )}
        </span>
      ))}
      <button type="button" aria-label="Vider la sélection" disabled={running} onClick={() => { setLaunch(null); setConfirming(null); onClear(); }}
        className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-full text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
